/**
 * ScheduleModal Component
 *
 * Real date + time picker for scheduling a WhatsApp campaign.
 * Includes "best time to send" AI suggestions, timezone display,
 * and quick-pick presets (in 1h, tomorrow morning, next weekend).
 */

import { useState, useMemo } from "react";
import {
  Calendar,
  Clock,
  X,
  Sparkles,
  Send,
  Loader2,
  Sunrise,
  Sun,
  Moon,
  Zap,
} from "lucide-react";

interface ScheduleModalProps {
  open: boolean;
  recipientCount: number;
  sending: boolean;
  onSchedule: (isoTimestamp: string) => void;
  onCancel: () => void;
}

/** Format Date → input[type=date] value (YYYY-MM-DD) */
function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Format Date → input[type=time] value (HH:MM) */
function toTimeInput(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

/** Combine date + time strings into a Date */
function combine(date: string, time: string): Date {
  return new Date(`${date}T${time}:00`);
}

export function ScheduleModal({
  open,
  recipientCount,
  sending,
  onSchedule,
  onCancel,
}: ScheduleModalProps) {
  const now = new Date();
  const defaultDate = new Date(now.getTime() + 60 * 60 * 1000); // +1h

  const [date, setDate] = useState<string>(toDateInput(defaultDate));
  const [time, setTime] = useState<string>(toTimeInput(defaultDate));
  const [error, setError] = useState<string | null>(null);

  const selectedDate = useMemo(() => combine(date, time), [date, time]);
  const isInFuture = selectedDate.getTime() > Date.now();
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // AI suggested time (heuristic for now: 11 AM weekday).
  // Tiene que quedar ANTES del early return: el modal se monta cerrado y se
  // abre por prop, y un hook después del return cambia la cantidad de hooks
  // entre renders («Rendered more hooks…»), lo que tira la pantalla entera.
  const aiSuggestion = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    // Skip to next weekday if tomorrow is weekend
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    d.setHours(11, 0, 0, 0);
    return d;
  }, []);

  if (!open) return null;

  /* ─── Quick presets ─── */
  const presets = [
    {
      label: "En 1 hora",
      icon: Zap,
      compute: () => new Date(Date.now() + 60 * 60 * 1000),
    },
    {
      label: "Mañana 9 AM",
      icon: Sunrise,
      compute: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
    {
      label: "Mañana 7 PM",
      icon: Sun,
      compute: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(19, 0, 0, 0);
        return d;
      },
    },
    {
      label: "Sábado 11 AM",
      icon: Moon,
      compute: () => {
        const d = new Date();
        const daysUntilSaturday = (6 - d.getDay() + 7) % 7 || 7;
        d.setDate(d.getDate() + daysUntilSaturday);
        d.setHours(11, 0, 0, 0);
        return d;
      },
    },
  ];

  const applyPreset = (preset: (typeof presets)[number]) => {
    const d = preset.compute();
    setDate(toDateInput(d));
    setTime(toTimeInput(d));
    setError(null);
  };

  const handleConfirm = () => {
    if (!isInFuture) {
      setError("Elegí una fecha y hora futuras");
      return;
    }
    onSchedule(selectedDate.toISOString());
  };

  const displayLocal = selectedDate.toLocaleString("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div role="dialog" aria-modal="true" aria-label="Programar envío" className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Calendar size={16} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-slate-900">
                Programar Envío
              </h3>
              <p className="text-xs text-slate-500">
                {recipientCount.toLocaleString()} destinatarios recibirán el
                mensaje en este momento
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            disabled={sending}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* AI suggestion */}
          <button
            onClick={() => {
              setDate(toDateInput(aiSuggestion));
              setTime(toTimeInput(aiSuggestion));
              setError(null);
            }}
            className="w-full p-4 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 rounded-xl text-left group hover:border-violet-200 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">
                  Sugerencia de IA
                </p>
                <p className="text-sm font-medium text-slate-900 mt-0.5">
                  {aiSuggestion.toLocaleString("es-AR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}{" "}
                  · 11:00
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Mejor engagement histórico de tu audiencia
                </p>
              </div>
              <span className="text-xs text-violet-600 font-medium opacity-0 group-hover:opacity-100 transition">
                Usar →
              </span>
            </div>
          </button>

          {/* Quick presets */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
              Atajos
            </p>
            <div className="grid grid-cols-4 gap-2">
              {presets.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p)}
                  className="p-3 bg-slate-50 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 rounded-xl text-center transition-all group"
                >
                  <p.icon
                    size={16}
                    className="text-slate-400 group-hover:text-emerald-500 mx-auto mb-1"
                  />
                  <p className="text-[10px] font-medium text-slate-600 group-hover:text-emerald-700 leading-tight">
                    {p.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">
                Fecha
              </label>
              <div className="relative">
                <Calendar
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="date"
                  value={date}
                  min={toDateInput(now)}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setError(null);
                  }}
                  className="w-full pl-9 pr-3 py-3 border border-slate-200 rounded-xl text-sm text-slate-900
                    focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">
                Hora
              </label>
              <div className="relative">
                <Clock
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="time"
                  value={time}
                  onChange={(e) => {
                    setTime(e.target.value);
                    setError(null);
                  }}
                  className="w-full pl-9 pr-3 py-3 border border-slate-200 rounded-xl text-sm text-slate-900
                    focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Preview line */}
          <div className="px-4 py-3 bg-emerald-50/50 rounded-xl border border-emerald-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-500">
                Se enviará el
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {tzName}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-900 capitalize">
              {displayLocal}
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-500 px-1">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onCancel}
            disabled={sending}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={sending || !isInFuture}
            className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white
              ${
                sending || !isInFuture
                  ? "bg-emerald-300 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/25 active:scale-[0.98]"
              }`}
          >
            {sending ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Programando...
              </>
            ) : (
              <>
                <Send size={14} />
                Programar Envío
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleModal;
